import React from 'react';
import { useAccomodationDiagram } from './AccomodationDiagramContext';
import { useMutation, useQuery } from 'react-fetching-library';
import { getAvailableOrdersToAccomodateAction, submitAccomodationDiagramAction } from '../../../services/request';
import { Box, Button, Divider, TextField, Menu, MenuItem, Stack, Tooltip, Typography } from '@mui/material';
import UpIcon from '@mui/icons-material/KeyboardArrowUp';
import DownIcon from '@mui/icons-material/KeyboardArrowDown';
import FemaleIcon from '@mui/icons-material/Female';
import MaleIcon from '@mui/icons-material/Male';
import ListIcon from '@mui/icons-material/List';
import SearchIcon from '@mui/icons-material/Search';
import { useReactFlow } from '@xyflow/react';
import { calculateAge } from '../../../services/functions';
import Iconify from '../../../components/Iconify';
import toast from 'react-hot-toast';
import { OrderContextMenu } from './context-menu/OrderContextMenu';
import { EventRecordContextMenu } from './context-menu/EventRecordContextMenu';
import { useApp } from '../../../AppContext';
import { CssTextField } from '../../../components/others/CssTextField';
import { DebounceInput } from 'react-debounce-input';

export const Sidebar = () => {
  const { edges, setOrder, orders, setOrders, selectedAccomodationDiagram, selectedEventGroup,
    nodes, setShowOrderDetails, setEventRecord, events, setSelectedAccomodationDiagram,
    setIsSaving, recalculateAllHouses, relatedGroups,
    showDiagramDataSyncDialog, setShowDiagramDataSyncDialog, savingDisabled, showOwnershipDialog, setShowOwnershipDialog,
    queryOrdersSignal, setQueryOrdersSignal, setSavingDisabled, changedPrefixes, setChangedPrefixes,
    initOrders, setInitOrders, updateNodesF } = useAccomodationDiagram();
  const { setCenter } = useReactFlow();
  const { ownershipHash } = useApp();
  const { loading: mutationLoading, mutate } = useMutation(submitAccomodationDiagramAction);
  const { loading, payload, error, query: queryOrders } = useQuery(getAvailableOrdersToAccomodateAction(selectedAccomodationDiagram.id));
  const [orderContextMenu, setOrderContextMenu] = React.useState(null);
  const [eventContextMenu, setEventContextMenu] = React.useState(null);
  const intervalRef = React.useRef(null);
  const [searchText, setSearchText] = React.useState('');
  const [nodesToFocus, setNodesToFocus] = React.useState([]);
  const [currentFocusedNodeIndex, setCurrentFocusedNodeIndex] = React.useState(0);


  React.useEffect(() => {
    setNodesToFocus(nodes.filter(n => n.data.order?.shared_accommodation_changed));
    setCurrentFocusedNodeIndex(0);
  }, [nodes]);

  const onDragStart = (event, order) => {
    setOrder(order);
    event.dataTransfer.effectAllowed = 'move';
  };

  const handleSubmit = async () => {
    setIsSaving(true);
    const updatedNodes = nodes.map(node => {
      if (node.type === 'BedNode') {
        const { backgroundColor, ...restStyle } = node.style || {};
        if (backgroundColor == 'red') {
          return {
            ...node,
            style: {
              ...restStyle
            }
          };
        }

        return {
          ...node,
          style: {
            ...restStyle,
            backgroundColor
          }
        };
      }

      return node;
    });

    updateNodesF(updatedNodes);
    setSearchText('');
    await new Promise(resolve => setTimeout(resolve, 1000));
    const updatedDiagramData = selectedAccomodationDiagram.diagram_data;
    updatedDiagramData.nodes = updatedNodes;
    updatedDiagramData.edges = edges;
    updatedDiagramData.events = events;
    updatedDiagramData.related_groups = relatedGroups;
    const { payload, error, status } = await mutate(
      { diagram_data: updatedDiagramData,
        init_orders: initOrders,
        final_orders: orders,
        should_recalculate_houses_positions: recalculateAllHouses,
        ownershipHash,
        changedPrefixes },
      'PATCH',
      selectedAccomodationDiagram.id);
    if (!error) {
      toast.success('Pomyślnie zapisano zmiany diagramu');
      setChangedPrefixes({});
    } else if (status === 426) {
      setShowDiagramDataSyncDialog(true);
      setSelectedAccomodationDiagram(payload);
      queryOrders(payload.id);
      setChangedPrefixes({});
    } else if (status === 428) {
      setShowOwnershipDialog(true);
      setSavingDisabled(true);
      setChangedPrefixes({});
    } else {
      toast.error('Nie udało się zapisać diagramu');
    }
    setIsSaving(false);
  };

  React.useEffect(() => {
    if (!loading && !error) {
      const ordersWithRelGroups = payload.orders.map(o => {
        relatedGroups.forEach(rg => {
          if (rg.orders.includes(o.identifier)) {
            const rgWithoutOrders = { ...rg };
            Reflect.deleteProperty(rgWithoutOrders, 'orders');
            o.relatedGroup = rgWithoutOrders;
          }
        });

        return o;
      });
      setOrders(ordersWithRelGroups);
      setInitOrders(ordersWithRelGroups);
    }
  }, [loading]);

  React.useEffect(() => {
    // Uruchom automatyczne zapisywanie co 30 sekund
    intervalRef.current = setInterval(() => {
      if (!showDiagramDataSyncDialog && !savingDisabled) {
        handleSubmit();
      }
    }, 30000);

    return () => {
      // Wyczyszczenie interwału przy odmontowaniu komponentu
      clearInterval(intervalRef.current);
    };
  }, [nodes, edges, events, initOrders, orders, selectedAccomodationDiagram, relatedGroups, showDiagramDataSyncDialog, savingDisabled, recalculateAllHouses, ownershipHash, showOwnershipDialog, changedPrefixes]);

  React.useEffect(() => {
    if (queryOrdersSignal) {
      queryOrders(selectedAccomodationDiagram.id);
      setQueryOrdersSignal(false);
    }
  }, [queryOrdersSignal]);


  const selectedEventGroupStyle = {
    border: '1px solid #00ff40'
  };

  const handleOrderContextMenu = (event, order) => {
    event.preventDefault();
    setOrder(order);
    setOrderContextMenu(
      orderContextMenu === null
        ? {
          mouseX: event.clientX - 2,
          mouseY: event.clientY - 4,
          order
        }
        : null
    );
  };

  const handleEventContextMenu = (event, eventRecord) => {
    event.preventDefault();
    setEventRecord(eventRecord);
    setEventContextMenu(
      eventContextMenu === null
        ? {
          mouseX: event.clientX - 2,
          mouseY: event.clientY - 4,
          eventRecord
        }
        : null
    );
  };

  const getEventName = order => {
    const truncateName = name => name.length > 20 ? `${name.slice(0, 20)}...` : name;

    return (
      <Typography color="default" variant="body25">
        <i>&nbsp;{truncateName(order.event__name)}</i>
      </Typography>
    );
  };

  function findGroupColorById(groupId) {
    for (const event of events) {
      for (const group of event.groups) {
        if (group.id === groupId) {
          return group.color;
        }
      }
    }

    return null;
  }

  const handleSearch = inputText => {
    const text = inputText.trim().toLowerCase();

    // Zaktualizuj kolory
    const updatedNodes = nodes.map(node => {
      if (text && node.data?.order?.participant_name.toLowerCase().includes(text)) {
        return {
          ...node,
          style: {
            ...node.style,
            backgroundColor: 'red'
          }
        };
      }

      if (node.type === 'BedNode') {
        const { backgroundColor, ...restStyle } = node.style || {};

        return { ...node, style: { ...restStyle } };
      }

      return node;
    });

    updateNodesF(updatedNodes);

    // Znajdź dopasowane nody
    const nodesToFocus = updatedNodes.filter(
      node => text && node.data?.order?.participant_name.toLowerCase().includes(text)
    );


    if (nodesToFocus.length === 0) {
      return;
    }

    // Weź pierwszy dopasowany node
    const currentNode = nodesToFocus[currentFocusedNodeIndex] || nodesToFocus[0];
    if (!currentNode) {
      return;
    }

    // Oblicz współrzędne absolutne
    let absoluteX = currentNode.position.x;
    let absoluteY = currentNode.position.y;
    let parentId = currentNode.parentId;

    while (parentId) {
      // eslint-disable-next-line no-loop-func
      const parentNode = updatedNodes.find(n => n.id === parentId);
      if (parentNode) {
        absoluteX += parentNode.position.x;
        absoluteY += parentNode.position.y;
        parentId = parentNode.parentId;
      } else {
        break;
      }
    }

    // Ustaw widok na współrzędne
    setCenter(absoluteX + 150, absoluteY + 50, {
      zoom: 2,
      duration: 800
    });

    // Przejdź do kolejnego node’a
    setCurrentFocusedNodeIndex(prev => (prev + 1) % nodesToFocus.length);
  };


  return (
    <aside style={{ height: '80vh' }}>
      <Stack sx={{ mb: 1 }} spacing={2}>
        <DebounceInput
          debounceTimeout={800}
          element={CssTextField}
          placeholder="Wyszukaj..."
          value={searchText}
          onChange={e => {
            setSearchText(e.target.value);
            handleSearch(e.target.value);
          }}
          variant="outlined"
        />
        <Button
          fullWidth
          variant="contained"
          endIcon={<Iconify icon={'eva:save-fill'} />}
          onClick={handleSubmit}
          disabled={mutationLoading || savingDisabled || loading}
        >
          Zapisz diagram
        </Button>
      </Stack>
      <Stack sx={{ height: '40vh' }}>
        <Typography variant="subtitle1" align="center" mb={1}>
            Lista niezakwaterowanych osób
        </Typography>
        <Divider />
        <Stack sx={{ overflow: 'auto' }}>
          {orders && orders.filter(o => !o.temporary).map((order, key) => (
            <div
              key={key}
              className="dndnode"
              onDragStart={event => onDragStart(event, order)}
              draggable
              onClick={() => {
                setShowOrderDetails(order);
                setOrder(order);
              }}
              onContextMenu={event => handleOrderContextMenu(event, order)}
              style={order.relatedGroup ? { border: `5px solid ${order.relatedGroup.color}` } : {}}
            >

              <Tooltip title={order.event__age_range__name} sx={{ ml: 1 }}>
                <Stack direction="column">
                  <Stack direction="row">
                    {order.participant_gender === 'boy' ? <MaleIcon /> : <FemaleIcon />}
                    {order.shared_accommodation_changed && <Typography color="red"><b>!</b></Typography>}

                    <Stack direction="row" spacing={0.5}>
                      <Typography variant="body2" color="red">{order.accommodation_prefix}</Typography>
                      <Typography variant="body2" color={findGroupColorById(order.accommodation_event_group)}><b>{order.participant_name}</b></Typography>
                    </Stack>
                  </Stack>
                  <Stack direction="row" alignItems="center" >
                    <Typography variant="body25"><i>{calculateAge(order.participant_birth_date, false, selectedAccomodationDiagram.camp_date_from)}</i></Typography>
                    {getEventName(order)}
                  </Stack>
                </Stack>
              </Tooltip>
            </div>
          ))}
        </Stack>
      </Stack>
      <Stack sx={{ height: '40vh' }}>
        <Divider />
        <Stack direction="row" justifyContent="center" alignItems="center" mb={1}>
          <Typography variant="subtitle1" align="center">
            Lista imprez
          </Typography>
          {selectedEventGroup && <Typography variant="subtitle1" align="center" color="red" ml={1}>!</Typography>}
        </Stack>
        <Divider />
        {nodes && <Stack sx={{ overflow: 'auto' }}>
          { events.map((event, key) => <EventRecord
            key={key}
            event={event}
            nodes={nodes}
            handleEventContextMenu={handleEventContextMenu}
            selectedEventGroupStyle={selectedEventGroupStyle}
          />
          )}
        </Stack>}
      </Stack>
      { eventContextMenu && !savingDisabled && <EventRecordContextMenu contextMenu={eventContextMenu} closeContextMenu={() => setEventContextMenu(null)} />}
      { orderContextMenu && !savingDisabled && <OrderContextMenu contextMenu={orderContextMenu} closeContextMenu={() => setOrderContextMenu(null)} />}
    </aside>
  );
};

const EventRecord = ({ event, nodes, handleEventContextMenu, selectedEventGroupStyle }) => {
  const [freeBeds, setFreeBeds] = React.useState(0);
  const [takenBeds, setTakenBeds] = React.useState(0);
  const [showGroups, setShowGroups] = React.useState(false);

  React.useEffect(() => {
    setFreeBeds(nodes.filter(n => n.data?.eventId == event.id && n.type == 'BedNode').length);
    setTakenBeds(nodes.filter(n => n.data?.eventId == event.id && n.type == 'BedNode' && n.data?.order).length);
  }, [nodes]);

  return (
    <div
      className="dndnode"
      style={{ display: 'flex', flexDirection: 'column' }}
      onContextMenu={e => handleEventContextMenu(e, event)}
    >
      <Stack direction="row" spacing={1}>
        <Typography variant="body2">
          <b>{event.name}</b>
        </Typography>
        <Stack direction="column" alignItems="center">
          <Typography color={freeBeds == 0 ? undefined : takenBeds == freeBeds ? 'error' : 'primary'}>
            {`(${takenBeds}/${freeBeds})`}
          </Typography>
          {event.groups.length > 0 && <Box onClick={() => setShowGroups(!showGroups)}>
            {showGroups ? <Tooltip title="Zwiń" placement="top" ><UpIcon /></Tooltip> : <Tooltip title="Rozwiń"><DownIcon /></Tooltip>}
          </Box>}
        </Stack>
      </Stack>
      {showGroups && <Stack direction="column" mt={1}>
        {event.groups.map((eg, key) => <EventGroupRecord
          key={key}
          eventGroup={eg}
          eventRecord={event}
          selectedEventGroupStyle={selectedEventGroupStyle}
        />)}
      </Stack>}
    </div>
  );
};

const EventGroupRecord = ({ eventGroup, eventRecord, selectedEventGroupStyle }) => {
  const { events, setEvents, setEventRecord, nodes,
    selectedEventGroup, setSelectedEventGroup, setShowEventRecordGroupMenu } = useAccomodationDiagram();
  const [freeBeds, setFreeBeds] = React.useState(0);
  const [takenBeds, setTakenBeds] = React.useState(0);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleEditEventGroup = () => {
    setSelectedEventGroup({ eventRecord, eventGroup });
    setEventRecord(eventRecord);
    setShowEventRecordGroupMenu(true);
    handleClose();
  };

  const handleRemoveEventGroup = () => {
    const accommodatedBeds = nodes.filter(n => n.type === 'BedNode' && n.data.order && n.data.eventGroup.id === eventGroup.id);
    if (accommodatedBeds.length > 0) {
      toast.error('Aby usunąć grupę należy najpierw wykwaterować wszystkie zamówienia przypisane do niej');

      return null;
    }
    const updatedEvents = events.map(ev => {
      if (ev.id === eventRecord.id) {
        const updatedGroups = ev.groups.filter(gr => gr.id != eventGroup.id);

        return {
          ...ev,
          groups: updatedGroups
        };
      }

      return ev;
    });
    setEvents(updatedEvents);
    handleClose();
  };

  const handleEventGroupClick = () => {
    if (selectedEventGroup) {
      setSelectedEventGroup(null);
    } else {
      // Potrzebujemy dane zarówno imprezy, jak i grupy
      setSelectedEventGroup({ eventRecord, eventGroup });
    }
  };

  React.useEffect(() => {
    setFreeBeds(nodes.filter(n => n.data?.eventGroup?.id == eventGroup.id && n.type == 'BedNode').length);
    setTakenBeds(nodes.filter(n => n.data?.eventGroup?.id == eventGroup.id && n.type == 'BedNode' && n.data?.order).length);
  }, [nodes]);

  return (
    <div className="event-group" style={selectedEventGroup?.eventGroup.id == eventGroup.id ? { ...selectedEventGroupStyle } : undefined}>
      <Stack direction="row" spacing={1} flex="1 1 90%" alignItems="center" justifyContent="left" onClick={handleEventGroupClick}>
        <Typography variant="body2" color={eventGroup.color}>
          <b>{eventGroup.name}</b>
        </Typography>
        <Typography color={freeBeds == 0 ? undefined : takenBeds == freeBeds ? 'error' : 'primary'}>
          {`(${takenBeds}/${freeBeds}/${eventGroup.slotsCount || ''})`}
        </Typography>
      </Stack>
      <Stack flex="1 1 10%" direction="row">
        <Divider orientation="vertical" />
        <ListIcon sx={{ cursor: 'pointer' }} onClick={handleClick} />
        <Menu
          id={eventGroup.id}
          MenuListProps={{
            'aria-labelledby': eventGroup.id
          }}
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
        >
          <MenuItem onClick={handleEditEventGroup}>
                    Edytuj
          </MenuItem>
          <MenuItem onClick={handleRemoveEventGroup}>
                    Usuń
          </MenuItem>
        </Menu>
      </Stack>
    </div>
  );
};
